/**
 * UserRepository — data access for the users table.
 *
 * All Supabase queries for user records are encapsulated
 * here. The UI and hooks never call Supabase directly.
 */

import { getSupabase } from '@/lib/supabase';
import { performanceService, globalErrorService } from '@/lib';
import type { UserRow } from '@/types/database';

export interface UserRepository {
  findById(id: string): Promise<UserRow | null>;
  findByEmail(email: string): Promise<UserRow | null>;
  create(data: Omit<UserRow, 'created_at' | 'updated_at'>): Promise<UserRow | null>;
  updateEmailVerified(id: string, verified: boolean): Promise<UserRow | null>;
}

export class SupabaseUserRepository implements UserRepository {
  async findById(id: string): Promise<UserRow | null> {
    return performanceService.measure(
      'repo.user.findById',
      async () => {
        const supabase = getSupabase();
        const { data, error } = await supabase
          .from('users')
          .select('*')
          .eq('id', id)
          .maybeSingle();

        if (error) {
          globalErrorService.capture(error, 'repository', { operation: 'findById', id });
          return null;
        }
        return data as UserRow | null;
      },
      { id },
    );
  }

  async findByEmail(email: string): Promise<UserRow | null> {
    return performanceService.measure(
      'repo.user.findByEmail',
      async () => {
        const supabase = getSupabase();
        const { data, error } = await supabase
          .from('users')
          .select('*')
          .eq('email', email)
          .maybeSingle();

        if (error) {
          globalErrorService.capture(error, 'repository', { operation: 'findByEmail' });
          return null;
        }
        return data as UserRow | null;
      },
    );
  }

  async create(data: Omit<UserRow, 'created_at' | 'updated_at'>): Promise<UserRow | null> {
    return performanceService.measure(
      'repo.user.create',
      async () => {
        const supabase = getSupabase();
        const { data: result, error } = await supabase
          .from('users')
          .insert(data)
          .select()
          .maybeSingle();

        if (error) {
          globalErrorService.capture(error, 'repository', { operation: 'create' });
          return null;
        }
        return result as UserRow | null;
      },
    );
  }

  async updateEmailVerified(id: string, verified: boolean): Promise<UserRow | null> {
    return performanceService.measure(
      'repo.user.updateEmailVerified',
      async () => {
        const supabase = getSupabase();
        const { data, error } = await supabase
          .from('users')
          .update({ email_verified: verified })
          .eq('id', id)
          .select()
          .maybeSingle();

        if (error) {
          globalErrorService.capture(error, 'repository', { operation: 'updateEmailVerified', id });
          return null;
        }
        return data as UserRow | null;
      },
      { id },
    );
  }
}
